/* global google */
import React, { useEffect, useRef, useState } from "react";
import proj4 from "proj4";

import "./showLayers.css";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export default function ShowLayers() {
    const mapRef = useRef(null);
    const [map, setMap] = useState(null);

    proj4.defs(
        "EPSG:26917",
        "+proj=utm +zone=17 +datum=NAD83 +units=m +no_defs"
    );
    const reprojectGeoJSON_26917_to_4326 = (geojson) => {
        const converted = JSON.parse(JSON.stringify(geojson));

        converted.features.forEach((feature) => {
            const geom = feature.geometry;

            if (geom.type === "LineString") {
                geom.coordinates = geom.coordinates.map(([x, y]) => {
                    const [lng, lat] = proj4("EPSG:26917", "EPSG:4326", [x, y]);
                    return [lng, lat];
                });
            }

            if (geom.type === "MultiLineString") {
                geom.coordinates = geom.coordinates.map((line) =>
                    line.map(([x, y]) => {
                        const [lng, lat] = proj4("EPSG:26917", "EPSG:4326", [x, y]);
                        return [lng, lat];
                    })
                );
            }
        });

        // CRS اصلاح شود
        delete converted.crs;

        return converted;
    };



    // =====================
    // Layer States
    // =====================
    const [layers, setLayers] = useState({
        corridorCenter: false,
        naturalFeature: false,

        esaTrca: false,
        regionalForest: false,
        ecoForest: false,
        greenlandSystem: false,

        greenLand: false,
        urbanGrowth: false,
        busStations: false,
        rapidTransit: false,
        trails: false,
        hospitals: false,
        cityFacilities: false,
        childrenServices: false,
        zoning: false,
    });

    // نگه‌داشتن feature های اضافه شده برای هر لایه (برای remove)
    const layerRefs = useRef({
        corridorCenter: null,
        esaTrca: null,
        regionalForest: null,
        ecoForest: null,
        greenlandSystem: null,
        busStations: null,
        trails: null,
        hospitals: null,
        childrenServices: null, // ✅ اضافه شود
        surfaceRapidTransit: null,


    });

    // برای جلوگیری از چندبار addListener
    const infoWindowRef = useRef(null);
    const clickListenerRef = useRef(null);

    // =====================
    // Load Google Map
    // =====================
    useEffect(() => {
        const loadMapScript = () =>
            new Promise((resolve) => {
                if (window.google) return resolve();
                const script = document.createElement("script");
                script.src = "https://maps.googleapis.com/maps/api/js?key=" + GOOGLE_API_KEY;
                script.onload = resolve;
                document.body.appendChild(script);
            });

        loadMapScript().then(() => {
            const mapInstance = new google.maps.Map(mapRef.current, {
                center: { lat: 43.7, lng: -79.4 },
                zoom: 9,
                mapTypeId: "roadmap",
            });
            setMap(mapInstance);
        });
    }, []);

    // ==========================================================
    // ✅ ONE GLOBAL STYLE (فقط یک بار) بر اساس __layer
    // ==========================================================
    useEffect(() => {
        if (!map) return;

        map.data.setStyle((feature) => {
            const layer = feature.getProperty("__layer");
            const geomType = feature.getGeometry().getType();

            // 🏥 Hospitals
            if (layer === "hospitals") {
                return {
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 6,
                        fillColor: "#FF0000",
                        fillOpacity: 1,
                        strokeColor: "#FFFFFF",
                        strokeWeight: 1,
                    },
                };
            }


            // 🚌 Bus Stations
            if (layer === "busStations" && geomType === "Point") {
                return {
                    icon: {
                        url: "https://maps.google.com/mapfiles/ms/icons/bus.png",
                        scaledSize: new google.maps.Size(26, 26),
                    },
                };
            }

            // 🟤 Trails
            if (layer === "trails" && geomType === "LineString") {
                return {
                    strokeColor: "#8D6E63",
                    strokeWeight: 3,
                    strokeOpacity: 0.9,
                };
            }

            // 🟦 Corridor & Centre
            if (layer === "corridorCenter" && (geomType === "Polygon" || geomType === "MultiPolygon")) {
                return {
                    fillColor: "#33C1FF",
                    strokeColor: "#0066cc",
                    strokeWeight: 2,
                    fillOpacity: 0.5,
                };
            }

            // 🟢 ESA TRCA
            if (layer === "esaTrca" && (geomType === "Polygon" || geomType === "MultiPolygon")) {
                return {
                    fillColor: "#8BC34A",
                    strokeColor: "#33691E",
                    fillOpacity: 0.45,
                    strokeWeight: 1.2,
                };
            }

            // 🌲 Regional Forest
            if (layer === "regionalForest" && (geomType === "Polygon" || geomType === "MultiPolygon")) {
                return {
                    fillColor: "#2E7D32",
                    strokeColor: "#1B5E20",
                    fillOpacity: 0.4,
                    strokeWeight: 1.3,
                };
            }

            // 🌿 Eco Forest
            if (layer === "ecoForest" && (geomType === "Polygon" || geomType === "MultiPolygon")) {
                return {
                    fillColor: "#81C784",
                    strokeColor: "#1B5E20",
                    fillOpacity: 0.4,
                    strokeWeight: 1.3,
                };
            }

            // 🍃 Greenland System
            if (layer === "greenlandSystem" && (geomType === "Polygon" || geomType === "MultiPolygon")) {
                return {
                    fillColor: "#A5D6A7",
                    strokeColor: "#2E7D32",
                    strokeWeight: 1.5,
                    fillOpacity: 0.35,
                };
            }
            // 👶 Children Services
            if (layer === "childrenServices") {
                return {
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 5,
                        fillColor: "#1976D2",   // آبی
                        fillOpacity: 0.9,
                        strokeColor: "#FFFFFF",
                        strokeWeight: 1,
                    },
                };
            }

            // 🚊 Surface Rapid Transit
            if (layer === "surfaceRapidTransit" && geomType === "LineString") {
                return {
                    strokeColor: "#fd00dbff",
                    strokeWeight: 4,
                    strokeOpacity: 0.9,
                };
            }


            // default
            return { visible: true };
        });
    }, [map]);

    // ==========================================================
    // ✅ ONE CLICK LISTENER (فقط یک بار)
    // ==========================================================
    useEffect(() => {
        if (!map) return;

        if (!infoWindowRef.current) infoWindowRef.current = new google.maps.InfoWindow();

        // اگر قبلاً listener اضافه شده، دوباره اضافه نکن
        if (clickListenerRef.current) return;

        clickListenerRef.current = map.data.addListener("click", (event) => {
            const feature = event.feature;
            const layer = feature.getProperty("__layer");
            const geomType = feature.getGeometry().getType();

            // اگر layer خاموشه، کلیک کاری نکن
            if (!layers[layer]) return;

            // 🚌 Bus popup
            if (layer === "busStations" && geomType === "Point") {
                const p = feature.getProperties();
                infoWindowRef.current.setContent(`
          <div style="font-size:13px">
            <strong>${p.STOP_NAME || ""}</strong><br/>
            Stop ID: ${p.STOP_ID || ""}<br/>
            Wheelchair: ${p.WHEELCHAIR_BOARDING || ""}<br/>
            <small>${p.DESCRIPTION || ""}</small>
          </div>
        `);
                infoWindowRef.current.setPosition(feature.getGeometry().get());
                infoWindowRef.current.open(map);
                return;
            }

            // 🏥 Hospital popup
            if (layer === "hospitals" && geomType === "Point") {
                const p = feature.getProperties();
                infoWindowRef.current.setContent(`
          <div style="font-size:13px">
            <strong>${p.NAME || "Hospital"}</strong><br/>
            ${p.ADDRESS || ""}<br/>
            ${p.TOWN_NAME || ""}${p.POST_CODE ? ", " + p.POST_CODE : ""}<br/>
            <b>Phone:</b> ${p.PHONE_NO || "N/A"}<br/>
            <small>Status: ${p.STATUS || ""}</small>
          </div>
        `);
                infoWindowRef.current.setPosition(feature.getGeometry().get());
                infoWindowRef.current.open(map);
                return;
            }

            // 🟤 Trails popup
            if (layer === "trails" && geomType === "LineString") {
                const p = feature.getProperties();
                infoWindowRef.current.setContent(`
          <div style="font-size:13px">
            <strong>${p.TRAIL_NAME || "Trail"}</strong><br/>
            Length: ${p.LENGTH ? Math.round(p.LENGTH) : "N/A"} m<br/>
            Public: ${p.PUBLIC_ || "N/A"}<br/>
            Material: ${p.MATERIAL || "N/A"}
          </div>
        `);

                const path = feature.getGeometry().getArray();
                infoWindowRef.current.setPosition(path[0]);
                infoWindowRef.current.open(map);
            }
        });

        // cleanup
        return () => {
            if (clickListenerRef.current) {
                google.maps.event.removeListener(clickListenerRef.current);
                clickListenerRef.current = null;
            }
        };
        // مهم: layers را dependency نمی‌ذاریم که listener هی recreate نشه
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]);

    // ==========================================================
    // Helper: add GeoJSON and tag features with __layer
    // ==========================================================
    const addLayerGeoJson = async (layerName, url) => {
        const res = await fetch(url);
        const geojson = await res.json();

        console.log(layerName, "features:", geojson?.features?.length);

        const addedFeatures = map.data.addGeoJson(geojson);
        // tag each added feature
        addedFeatures.forEach((f) => f.setProperty("__layer", layerName));
        layerRefs.current[layerName] = addedFeatures;
    };

    const removeLayer = (layerName) => {
        if (!layerRefs.current[layerName]) return;
        layerRefs.current[layerName].forEach((f) => map.data.remove(f));
        layerRefs.current[layerName] = null;
    };

    // ===========================================
    // Corridor & Centre
    // ===========================================
    useEffect(() => {
        if (!map) return;
        const layerName = "corridorCenter";
        const url =
            "https://ww8.yorkmaps.ca/arcgis/rest/services/EnterprisePublic/LandInformation/MapServer/9/query?where=1%3D1&outFields=*&f=geojson";

        if (layers[layerName] && !layerRefs.current[layerName]) {
            addLayerGeoJson(layerName, url);
        } else if (!layers[layerName] && layerRefs.current[layerName]) {
            removeLayer(layerName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layers.corridorCenter, map]);

    // ===========================================
    // Greenland System
    // ===========================================
    useEffect(() => {
        if (!map) return;
        const layerName = "greenlandSystem";
        const url =
            "https://ww8.yorkmaps.ca/arcgis/rest/services/EnterprisePublic/LandInformation/MapServer/7/query?where=1%3D1&outFields=*&f=geojson";

        if (layers[layerName] && !layerRefs.current[layerName]) {
            addLayerGeoJson(layerName, url);
        } else if (!layers[layerName] && layerRefs.current[layerName]) {
            removeLayer(layerName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layers.greenlandSystem, map]);

    // ===========================================
    // ESA TRCA
    // ===========================================
    useEffect(() => {
        if (!map) return;
        const layerName = "esaTrca";
        const url =
            "https://ww8.yorkmaps.ca/arcgis/rest/services/EnterprisePublic/LandInformation/MapServer/4/query?where=1%3D1&outFields=*&f=geojson";

        if (layers[layerName] && !layerRefs.current[layerName]) {
            addLayerGeoJson(layerName, url);
        } else if (!layers[layerName] && layerRefs.current[layerName]) {
            removeLayer(layerName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layers.esaTrca, map]);

    // ===========================================
    // Regional Forest
    // ===========================================
    useEffect(() => {
        if (!map) return;
        const layerName = "regionalForest";
        const url =
            "https://ww8.yorkmaps.ca/arcgis/rest/services/EnterprisePublic/LandInformation/MapServer/5/query?where=1%3D1&outFields=*&f=geojson";

        if (layers[layerName] && !layerRefs.current[layerName]) {
            addLayerGeoJson(layerName, url);
        } else if (!layers[layerName] && layerRefs.current[layerName]) {
            removeLayer(layerName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layers.regionalForest, map]);

    // ===========================================
    // Eco Forest
    // ===========================================
    useEffect(() => {
        if (!map) return;
        const layerName = "ecoForest";
        const url =
            "https://ww8.yorkmaps.ca/arcgis/rest/services/EnterprisePublic/General/MapServer/2/query?where=1%3D1&outFields=*&f=geojson";

        if (layers[layerName] && !layerRefs.current[layerName]) {
            addLayerGeoJson(layerName, url);
        } else if (!layers[layerName] && layerRefs.current[layerName]) {
            removeLayer(layerName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layers.ecoForest, map]);

    // ===========================================
    // Bus Stations (local geojson)
    // ===========================================
    useEffect(() => {
        if (!map) return;
        const layerName = "busStations";
        const url = "/Bus_Stops_from_GTFS.geojson";

        if (layers[layerName] && !layerRefs.current[layerName]) {
            addLayerGeoJson(layerName, url);
        } else if (!layers[layerName] && layerRefs.current[layerName]) {
            removeLayer(layerName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layers.busStations, map]);

    // ===========================================
    // Trails (local geojson)
    // ===========================================
    useEffect(() => {
        if (!map) return;
        const layerName = "trails";
        const url = "/Trails.geojson";

        if (layers[layerName] && !layerRefs.current[layerName]) {
            addLayerGeoJson(layerName, url);
        } else if (!layers[layerName] && layerRefs.current[layerName]) {
            removeLayer(layerName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layers.trails, map]);

    // ===========================================
    // Hospitals (local geojson)
    // ===========================================
    useEffect(() => {
        if (!map) return;
        const layerName = "hospitals";
        const url = "/Hospital.geojson";

        if (layers[layerName] && !layerRefs.current[layerName]) {
            addLayerGeoJson(layerName, url);
        } else if (!layers[layerName] && layerRefs.current[layerName]) {
            removeLayer(layerName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layers.hospitals, map]);

    // ===========================================
    // 👶 Children Services (local geojson)
    // ===========================================
    useEffect(() => {
        if (!map) return;

        const layerName = "childrenServices";
        const url = "/Children_s_Service.geojson";

        if (layers[layerName] && !layerRefs.current[layerName]) {
            addLayerGeoJson(layerName, url);
        } else if (!layers[layerName] && layerRefs.current[layerName]) {
            removeLayer(layerName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layers.childrenServices, map]);

    // ===========================================
    // 🚊 Surface Rapid Transit Service (UTM → WGS84)
    // ===========================================
    useEffect(() => {
        if (!map) return;

        const layerName = "surfaceRapidTransit";
        const url = "/SurfaceRapidTransitService.json";

        if (layers.rapidTransit && !layerRefs.current[layerName]) {
            fetch(url)
                .then((res) => res.json())
                .then((geojson) => {
                    console.log("Rapid Transit (raw):", geojson.features.length);

                    const converted = reprojectGeoJSON_26917_to_4326(geojson);

                    const added = map.data.addGeoJson(converted);
                    added.forEach((f) => f.setProperty("__layer", layerName));
                    layerRefs.current[layerName] = added;
                });

        } else if (!layers.rapidTransit && layerRefs.current[layerName]) {
            removeLayer(layerName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layers.rapidTransit, map]);









    // =====================
    // Handle Checkbox Change
    // =====================
    const handleLayerToggle = (layerName) => {
        setLayers((prev) => ({
            ...prev,
            [layerName]: !prev[layerName],
        }));
    };

    return (
        <div style={{ width: "100%", position: "relative" }}>
            <div className="top-bar">Show Layer of York Region</div>

            <div className="legend-box">
                <div className="legend-title">Layers</div>

                <div className="checkbox-item">
                    <input
                        type="checkbox"
                        checked={layers.corridorCenter}
                        onChange={() => handleLayerToggle("corridorCenter")}
                    />
                    Regional Corridor & Centre
                </div>
                <div className="checkbox-item sub-item">
                    <input
                        type="checkbox"
                        checked={layers.greenlandSystem}
                        onChange={() => handleLayerToggle("greenlandSystem")}
                    />
                    Regional Greenlands System
                </div>





                <div className="checkbox-item">
                    <input
                        type="checkbox"
                        checked={layers.busStations}
                        onChange={() => handleLayerToggle("busStations")}
                    />
                    Bus Stations
                </div>

                <div className="checkbox-item">
                    <input
                        type="checkbox"
                        checked={layers.rapidTransit}
                        onChange={() => handleLayerToggle("rapidTransit")}
                    />
                    Surface Rapid Transit Service
                </div>



                <div className="checkbox-item">
                    <input
                        type="checkbox"
                        checked={layers.hospitals}
                        onChange={() => handleLayerToggle("hospitals")}
                    />
                    Hospitals
                </div>



                <div className="checkbox-item">
                    <input
                        type="checkbox"
                        checked={layers.childrenServices}
                        onChange={() => handleLayerToggle("childrenServices")}
                    />
                    Children Services
                </div>


            </div>

            <div ref={mapRef} className="map-container" />
        </div>
    );
}
